import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Mail, MessageSquare, RotateCcw, Save, Info, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const EMAIL_FIELDS = [
  { key: "subject", label: "Subject line", rows: 1 },
  { key: "heading", label: "Heading", rows: 1 },
  { key: "intro", label: "Intro paragraph", rows: 3 },
  { key: "footer", label: "Sign-off / footer", rows: 2 },
];

function VariablesHelper({ vars }) {
  if (!vars?.length) return null;
  return (
    <div className="flex items-start gap-2 text-xs text-[#4A5D6B] bg-[#F1F5F8] rounded-lg px-3 py-2 mb-4">
      <Info size={13} className="mt-0.5 shrink-0 text-[#144F75]" />
      <span>Available variables (auto-filled): {vars.map((v) => <code key={v} className="mx-0.5 text-[#144F75]">{v}</code>)}</span>
    </div>
  );
}

function EmailCard({ t }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ subject: t.subject, heading: t.heading, intro: t.intro, footer: t.footer });
  const dirty = EMAIL_FIELDS.some((f) => (form[f.key] || "") !== (t[f.key] || ""));
  const save = useMutation({
    mutationFn: () => api.put(`/admin/email-templates/${t.key}`, form),
    onSuccess: () => { toast.success(`Saved "${t.label}"`); qc.invalidateQueries({ queryKey: ["email-templates"] }); },
    onError: () => toast.error("We couldn't save that template. Please try again."),
  });
  const reset = useMutation({
    mutationFn: () => api.delete(`/admin/email-templates/${t.key}`),
    onSuccess: () => { toast.success("Reset to default"); qc.invalidateQueries({ queryKey: ["email-templates"] }); },
  });
  return (
    <div className="eyw-card p-6" data-testid={`email-tpl-${t.key}`}>
      <div className="mb-4">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><Mail size={17} className="text-[#144F75]" /> {t.label}</h3>
        <p className="text-xs text-[#8FA89B] mt-0.5 uppercase tracking-wide">{t.group}{t.is_overridden ? " · customised" : " · default"}</p>
      </div>
      <VariablesHelper vars={t.variables} />
      <div className="space-y-3">
        {EMAIL_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-sm font-medium text-[#1A242B] mb-1 block">{f.label}</label>
            {f.rows === 1 ? (
              <input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                data-testid={`email-tpl-${t.key}-${f.key}`} className="eyw-input w-full text-sm" placeholder={t.defaults?.[f.key]} />
            ) : (
              <textarea value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                rows={f.rows} data-testid={`email-tpl-${t.key}-${f.key}`} className="eyw-input w-full text-sm" placeholder={t.defaults?.[f.key]} />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button onClick={() => save.mutate()} disabled={!dirty || save.isPending} data-testid={`email-tpl-${t.key}-save`}
          className="eyw-btn text-sm disabled:opacity-50 inline-flex items-center gap-2">
          {save.isPending ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
        </button>
        {t.is_overridden && (
          <button onClick={() => reset.mutate()} disabled={reset.isPending} data-testid={`email-tpl-${t.key}-reset`}
            className="text-sm text-[#B5794E] hover:underline inline-flex items-center gap-1.5"><RotateCcw size={14} /> Reset to default</button>
        )}
      </div>
    </div>
  );
}

function SmsCard({ t }) {
  const qc = useQueryClient();
  const [body, setBody] = useState(t.body || "");
  const dirty = (body || "") !== (t.body || "");
  const save = useMutation({
    mutationFn: () => api.put(`/admin/sms-templates/${t.key}`, { body }),
    onSuccess: () => { toast.success(`Saved "${t.label}"`); qc.invalidateQueries({ queryKey: ["sms-templates"] }); },
    onError: () => toast.error("We couldn't save that template. Please try again."),
  });
  const reset = useMutation({
    mutationFn: () => api.delete(`/admin/sms-templates/${t.key}`),
    onSuccess: () => { toast.success("Reset to default"); qc.invalidateQueries({ queryKey: ["sms-templates"] }); },
  });
  return (
    <div className="eyw-card p-6" data-testid={`sms-tpl-${t.key}`}>
      <div className="mb-4">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><MessageSquare size={17} className="text-[#5C8A72]" /> {t.label}</h3>
        <p className="text-xs text-[#8FA89B] mt-0.5 uppercase tracking-wide">{t.group}{t.is_overridden ? " · customised" : " · default"}</p>
      </div>
      <VariablesHelper vars={t.variables} />
      <label className="text-sm font-medium text-[#1A242B] mb-1 block">Message</label>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
        data-testid={`sms-tpl-${t.key}-body`} className="eyw-input w-full text-sm" placeholder={t.default_body} />
      <p className="text-xs text-[#8FA89B] mt-1">{body.length} characters {body.length > 160 ? "· may send as multiple texts" : ""}</p>
      <div className="flex items-center gap-3 mt-4">
        <button onClick={() => save.mutate()} disabled={!dirty || save.isPending} data-testid={`sms-tpl-${t.key}-save`}
          className="eyw-btn text-sm disabled:opacity-50 inline-flex items-center gap-2">
          {save.isPending ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
        </button>
        {t.is_overridden && (
          <button onClick={() => reset.mutate()} disabled={reset.isPending} data-testid={`sms-tpl-${t.key}-reset`}
            className="text-sm text-[#B5794E] hover:underline inline-flex items-center gap-1.5"><RotateCcw size={14} /> Reset to default</button>
        )}
      </div>
    </div>
  );
}

function TimingPanel({ form, setForm, onSave, saving }) {
  if (!form) return <div className="py-20 text-center"><Loader2 className="animate-spin inline text-[#144F75]" /></div>;

  const TIMING_CONFIGS = [
    {
      key: "reminder_24h_target",
      label: "Day-before reminder",
      description: "Send clients their first reminder this many hours before the appointment (Eastern Time).",
      min: 4, max: 48, step: 1, unit: "hours before",
      note: "Fires within ±1h of the target. Default: 24h.",
    },
    {
      key: "reminder_1h_target",
      label: "Same-day reminder",
      description: "Send clients a same-day nudge this many hours before the appointment (Eastern Time).",
      min: 0.5, max: 6, step: 0.5, unit: "hours before",
      note: "Fires within ±30 min of the target. Default: 1h.",
    },
    {
      key: "review_email_delay_hours",
      label: "Review request email",
      description: "Ask clients for a Google review this many hours after their session ends (Eastern Time).",
      min: 4, max: 72, step: 1, unit: "hours after",
      note: "Fires within ±2h of the target. Default: 24h. Runs automatically every hour.",
    },
  ];

  return (
    <div data-testid="timing-panel" className="space-y-6 max-w-2xl">
      <div className="eyw-card p-5 bg-[#F5F8FA] border border-[#144F75]/10">
        <div className="flex items-start gap-2 text-sm text-[#4A5D6B]">
          <Clock size={15} className="text-[#144F75] mt-0.5 shrink-0" />
          <p>All times are <strong>Eastern Time (ET)</strong>. Appointment start/end times are stored in ET, so reminders always fire at the right local time regardless of where the server runs.</p>
        </div>
      </div>

      {TIMING_CONFIGS.map(({ key, label, description, min, max, step, unit, note }) => {
        const val = Number(form[key] ?? (key === "reminder_1h_target" ? 1 : 24));
        return (
          <div key={key} className="eyw-card p-6" data-testid={`timing-${key}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2"><Clock size={15} className="text-[#144F75]" /> {label}</h3>
                <p className="text-sm text-[#4A5D6B] mt-0.5">{description}</p>
              </div>
              <div className="text-right shrink-0">
                <span className="text-2xl font-semibold text-[#144F75]">{val}</span>
                <span className="text-sm text-[#8FA89B] ml-1">{unit}</span>
              </div>
            </div>
            <input
              type="range"
              min={min} max={max} step={step}
              value={val}
              onChange={(e) => setForm({ ...form, [key]: Number(e.target.value) })}
              data-testid={`timing-${key}-slider`}
              className="w-full accent-[#144F75] h-2"
            />
            <div className="flex justify-between text-xs text-[#8FA89B] mt-1">
              <span>{min}h</span>
              <span>{max}h</span>
            </div>
            <p className="text-xs text-[#8FA89B] mt-2 italic">{note}</p>
          </div>
        );
      })}

      <button
        onClick={onSave}
        disabled={saving}
        data-testid="timing-save-btn"
        className="eyw-btn text-sm inline-flex items-center gap-2 disabled:opacity-50"
      >
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save timing settings
      </button>
    </div>
  );
}

export default function EmailTemplatesPanel() {
  const [mode, setMode] = useState("email");
  const emailQ = useQuery({ queryKey: ["email-templates"], queryFn: () => api.get("/admin/email-templates").then((r) => r.data) });
  const smsQ = useQuery({ queryKey: ["sms-templates"], queryFn: () => api.get("/admin/sms-templates").then((r) => r.data), enabled: mode === "sms" });
  const timingQ = useQuery({ queryKey: ["integrations-settings"], queryFn: () => api.get("/admin/integrations").then((r) => r.data), enabled: mode === "timing" });
  const qc = useQueryClient();

  const [timingForm, setTimingForm] = useState(null);
  const timingData = timingQ.data;
  // Sync form with fetched data
  if (mode === "timing" && timingData && !timingForm) {
    setTimingForm({
      reminder_24h_target: timingData.reminder_24h_target ?? 24,
      reminder_1h_target: timingData.reminder_1h_target ?? 1,
      review_email_delay_hours: timingData.review_email_delay_hours ?? 24,
    });
  }

  const saveTimingMut = useMutation({
    mutationFn: (data) => api.put("/admin/integrations", data),
    onSuccess: () => { toast.success("Timing settings saved"); qc.invalidateQueries({ queryKey: ["integrations-settings"] }); },
    onError: () => toast.error("Could not save timing settings"),
  });

  const loading = mode === "email" ? emailQ.isLoading : mode === "sms" ? smsQ.isLoading : timingQ.isLoading;

  return (
    <div data-testid="email-templates-panel">
      <div className="mb-6">
        <h2 className="font-serif text-2xl text-[#1A242B]">Message templates &amp; timing</h2>
        <p className="text-[#4A5D6B] text-sm mt-1 max-w-2xl">Edit the wording of your automatic emails and texts, and control exactly when each reminder fires — all Eastern Time.</p>
      </div>

      <div className="inline-flex rounded-full bg-[#F1EEE8] p-0.5 mb-6 flex-wrap gap-y-1" data-testid="template-mode-toggle">
        {[
          { k: "email", l: "Email", Icon: Mail },
          { k: "sms", l: "Text messages", Icon: MessageSquare },
          { k: "timing", l: "Timing", Icon: Clock },
        ].map((m) => (
          <button key={m.k} onClick={() => { setMode(m.k); if (m.k === "timing") setTimingForm(null); }}
            data-testid={`template-mode-${m.k}`}
            className={`px-4 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 transition-colors ${mode === m.k ? "bg-[#144F75] text-white" : "text-[#4A5D6B] hover:bg-[#E5E0D8]"}`}>
            <m.Icon size={14} /> {m.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="animate-spin inline text-[#144F75]" /></div>
      ) : mode === "email" ? (
        <div className="grid gap-6 lg:grid-cols-2">{(emailQ.data || []).map((t) => <EmailCard key={t.key} t={t} />)}</div>
      ) : mode === "sms" ? (
        <div className="grid gap-6 lg:grid-cols-2">{(smsQ.data || []).map((t) => <SmsCard key={t.key} t={t} />)}</div>
      ) : (
        <TimingPanel form={timingForm} setForm={setTimingForm} onSave={() => saveTimingMut.mutate(timingForm)} saving={saveTimingMut.isPending} />
      )}
    </div>
  );
}
